import type { Pagination, UserRole } from "@/types";

import { BaseApi } from "../base.api";

/**
 * Faollik jurnali — backend'dagi `activity-log.entity.ts` dagi kodlar.
 *
 * Kod TARJIMA qilinmagan holda keladi (backend matn qaytarmaydi); ekrandagi
 * jumla `ACTIVITY.ACTION_*` kalitidan va `meta` dagi qiymatlardan
 * yig'iladi.
 */
export const ACTIVITY_ACTIONS = [
  "AUTH_LOGIN",
  "AUTH_REGISTER",
  "AUTH_PASSWORD_CHANGED",
  "COURSE_CREATED",
  "COURSE_UPDATED",
  "COURSE_SUBMITTED",
  "COURSE_DELETED",
  "COURSE_ADMISSION_CLOSED",
  "COURSE_ADMISSION_OPENED",
  "COURSE_FINISHED",
  "SECTION_CREATED",
  "SECTION_UPDATED",
  "SECTION_DELETED",
  "LESSON_CREATED",
  "LESSON_UPDATED",
  "LESSON_DELETED",
  "SESSION_CREATED",
  "SESSION_UPDATED",
  "SESSION_CANCELLED",
  "SESSION_DELETED",
  "JOURNAL_SAVED",
  "SUBMISSION_GRADED",
  "ATTEMPT_GRADED",
  "STUDENT_REMOVED",
  "STUDENT_READMITTED",
  "COURSE_ENROLLED",
  "COURSE_LEFT",
  "ASSIGNMENT_SUBMITTED",
  "QUIZ_SUBMITTED",
  "COURSE_APPROVED",
  "COURSE_REJECTED",
  "POST_APPROVED",
  "POST_REJECTED",
  "REVIEW_APPROVED",
  "REVIEW_REJECTED",
  "TEACHER_REQUEST_APPROVED",
  "TEACHER_REQUEST_REJECTED",
  "COURSE_ARCHIVED",
  "USER_BLOCKED",
  "USER_UNBLOCKED",
  "USER_ROLE_CHANGED",
  "SETTINGS_UPDATED",
  "PAYMENT_CONFIRMED",
  "PAYMENT_REFUNDED",
  "PAYMENT_REJECTED",
  "REFUND_REJECTED",
  "WITHDRAWAL_PAID",
  "WITHDRAWAL_REJECTED",
] as const;
export type ActivityAction = (typeof ACTIVITY_ACTIONS)[number];

/** Filtr uchun guruhlar — backend'dagi `ACTIVITY_GROUPS` bilan bir xil. */
export const ACTIVITY_GROUPS = [
  "AUTH",
  "COURSE",
  "CONTENT",
  "LIVE",
  "GRADING",
  "STUDENTS",
  "LEARNING",
  "MODERATION",
  "ADMIN",
] as const;
export type ActivityGroup = (typeof ACTIVITY_GROUPS)[number];

export type ActivitySubject =
  | "USER"
  | "COURSE"
  | "SECTION"
  | "LESSON"
  | "SESSION"
  | "SUBMISSION"
  | "ATTEMPT"
  | "ENROLLMENT"
  | "POST"
  | "REVIEW"
  | "TEACHER_REQUEST"
  | "PAYMENT"
  | "SETTINGS";

export interface ActivityEntry {
  id: number;
  action: ActivityAction;
  group: ActivityGroup;
  subjectType: ActivitySubject | null;
  subjectId: number | null;
  /** Harakatga qarab: `title`, `fields`, `from`/`to`, jurnal o'zgarishlari va h.k. */
  meta: Record<string, unknown>;
  createdAt: string;
  actor: {
    id: number;
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
    role: UserRole;
  };
  course: { id: number; title: string } | null;
}

export interface ActivityQuery {
  page?: number;
  limit?: number;
  actorId?: number;
  courseId?: number;
  subjectType?: ActivitySubject;
  subjectId?: number;
  group?: ActivityGroup[];
  action?: ActivityAction[];
  from?: string;
  to?: string;
}

/**
 * Massivlar vergul bilan: `?group=AUTH,GRADING`. Axios standart holda
 * `group[]=AUTH&group[]=GRADING` yozadi va backend buni tanimaydi.
 */
const serialize = (query: ActivityQuery) => ({
  ...query,
  group: query.group?.length ? query.group.join(",") : undefined,
  action: query.action?.length ? query.action.join(",") : undefined,
});

/** Faollik jurnali API — backend'dagi `ActivityController` (faqat admin). */
export class ActivityApi extends BaseApi {
  private readonly baseUrl: string = "/activity";

  list(query: ActivityQuery) {
    return this.request.get<ActivityEntry[], Pagination<ActivityEntry>>(this.baseUrl, {
      params: serialize(query),
    });
  }
}

export const activityApi = new ActivityApi();
